/**
 * TasksModule - Page component for the Tasks module.
 *
 * Loads task data on mount (lazy hydration — data is only fetched when this
 * module activates via React.lazy). Renders the task list or matrix view
 * depending on the active view, plus filter controls and detail panel.
 *
 * @see Requirements 2.1, 3.1, 4.1, 13.3, 13.6
 */

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useTaskStore } from '@/stores/taskStore';
import { TaskListView } from '@/components/tasks/TaskListView';
import { TaskFilters } from '@/components/tasks/TaskFilters';
import { MatrixView } from '@/components/tasks/MatrixView';
import { TaskDetailPanel } from '@/components/tasks/TaskDetailPanel';
import { FloatingActionButton } from '@/components/ui/FloatingActionButton';

export default function TasksModule() {
  const tasks = useTaskStore((s) => s.tasks);
  const activeView = useTaskStore((s) => s.activeView);
  const isLoading = useTaskStore((s) => s.isLoading);
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const loadTasks = useTaskStore((s) => s.loadTasks);
  const selectTask = useTaskStore((s) => s.selectTask);
  const completeTask = useTaskStore((s) => s.completeTask);
  // Local draft state: when true the detail panel is open in create mode. No
  // task is persisted until the user titles it and saves (avoids junk rows).
  const [isDraftOpen, setIsDraftOpen] = useState(false);

  // Lazy hydration: load tasks when this module mounts
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Memoized: filter tasks for Today view (due today or overdue, not completed)
  const todayTasks = useMemo(() => {
    if (activeView !== 'today') return tasks;
    const today = new Date().toISOString().split('T')[0]!;
    return tasks.filter(
      (t) => !t.isCompleted && !t.deleted && t.dueDate && t.dueDate <= today
    );
  }, [tasks, activeView]);

  // Memoized: filter tasks for Upcoming (next 7 days) view
  const upcomingTasks = useMemo(() => {
    if (activeView !== 'upcoming') return tasks;
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]!;
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekStr = nextWeek.toISOString().split('T')[0]!;
    return tasks.filter(
      (t) =>
        !t.isCompleted &&
        !t.deleted &&
        t.dueDate &&
        t.dueDate > todayStr &&
        t.dueDate <= nextWeekStr
    );
  }, [tasks, activeView]);

  // Memoized: select the correct task list based on active view
  const displayedTasks = useMemo(() => {
    switch (activeView) {
      case 'today':
        return todayTasks;
      case 'upcoming':
        return upcomingTasks;
      default:
        return tasks;
    }
  }, [activeView, tasks, todayTasks, upcomingTasks]);

  const handleTaskClick = useCallback(
    (taskId: string) => {
      // Selecting an existing task leaves draft mode.
      setIsDraftOpen(false);
      selectTask(taskId);
    },
    [selectTask]
  );

  const handleComplete = useCallback(
    (taskId: string) => {
      completeTask(taskId);
    },
    [completeTask]
  );

  const handleCloseDetail = useCallback(() => {
    setIsDraftOpen(false);
    selectTask(null);
  }, [selectTask]);

  // Open the detail panel in draft/create mode. Nothing is written to the store
  // or server here — the task is only persisted once the user saves (R2.1).
  const handleFabClick = useCallback(() => {
    selectTask(null);
    setIsDraftOpen(true);
  }, [selectTask]);

  // After a draft task is created, select it so the panel shows the saved task.
  const handleDraftCreated = useCallback(
    (taskId: string) => {
      setIsDraftOpen(false);
      selectTask(taskId);
    },
    [selectTask]
  );

  return (
    <div className="relative flex flex-col h-full p-6 gap-4">
      {/* Header */}
      <h1 className="text-heading-1 font-extrabold text-white tracking-tight">Tasks</h1>

      {/* Filters and view switching */}
      <TaskFilters />

      {/* Main content area */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Task list or matrix view */}
        <div className="flex-1 min-h-0 relative">
          {isLoading && tasks.length === 0 && (
            <div
              className="flex items-center justify-center py-12"
              aria-busy="true"
              aria-label="Loading tasks"
            >
              <div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {activeView === 'eisenhower' ? (
            <MatrixView onTaskClick={handleTaskClick} />
          ) : (
            <TaskListView
              tasks={displayedTasks}
              onTaskClick={handleTaskClick}
              onComplete={handleComplete}
              onCreateTask={handleFabClick}
            />
          )}
        </div>

        {/* Detail panel (slides in when a task is selected or being created) */}
        {(selectedTaskId || isDraftOpen) && (
          <div className="w-[380px] flex-shrink-0 card-gradient overflow-y-auto max-h-full transition-all duration-200 ease-in-out">
            <TaskDetailPanel
              key={isDraftOpen ? 'new-task' : selectedTaskId}
              taskId={isDraftOpen ? undefined : selectedTaskId ?? undefined}
              draft={isDraftOpen}
              onClose={handleCloseDetail}
              onCreated={handleDraftCreated}
            />
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <FloatingActionButton
        onClick={handleFabClick}
        label="+ New Task"
        gradient="purple"
      />
    </div>
  );
}
